import { Errors } from '@lbcamden/errors';
import { Synchronizer } from 'tinybase/synchronizers';
import {
  MergeableStore,
  OptionalSchemas,
  Row as SchemaRow,
  Table as SchemaTable,
} from 'tinybase/with-schemas';

import {
  PartialRowWithId,
  PathPersister,
  RowIdKeys,
  TableRow,
  ZodTableMap,
} from '~/common/types/tinybase';

/**
 * TinyBase's setRow/setPartialRow/setTable mutate the row objects they are
 * given, replacing object cell values in place with an internal string
 * encoding. Rows are shallow-cloned before being passed in so callers'
 * objects stay untouched (e.g. a message payload that is both written to a
 * table and embedded in a `messages.payload` cell).
 */
const cloneRow = <TRow extends object>(row: TRow): TRow => ({ ...row });

export abstract class SyncStoreWrapper<
  TZodTables extends ZodTableMap,
  TStoreSchemas extends OptionalSchemas,
  TRowIdKeys extends RowIdKeys<TZodTables> = RowIdKeys<TZodTables>,
> {
  public readonly created = new Date();

  public constructor(
    public readonly store: MergeableStore<TStoreSchemas>,
    public readonly rowIdKeys: TRowIdKeys,
    public readonly persister?: PathPersister,
    public readonly synchronizer?: Synchronizer,
  ) {}

  public destroy() {
    void this.persister?.destroy();
    void this.synchronizer?.destroy();
  }

  public replaceAll<TTableId extends StringKey<TZodTables>>(
    tableId: TTableId,
    rows: TableRow<TZodTables, TTableId>[],
  ) {
    if (!rows.length) {
      this.store.delTable(tableId);
      return;
    }
    const idKey = this.rowIdKeys[tableId];
    const table: SchemaTable<TStoreSchemas[0], TTableId, true> = {};

    // for loop used for better performance when dealing with large datasets
    for (let i = 0; i < rows.length; i++) {
      const row = cloneRow(rows[i]) as SchemaRow<TStoreSchemas[0], TTableId, true>;
      table[row[idKey] as string] = row;
    }
    this.store.setTable(tableId, table);
  }

  public replaceByCellValue<
    TTableId extends StringKey<TZodTables>,
    TCellId extends StringKey<TableRow<TZodTables, TTableId>>,
  >(
    tableId: TTableId,
    cellId: StringKey<TableRow<TZodTables, TTableId>>,
    value: TableRow<TZodTables, TTableId>[TCellId],
    rows: TableRow<TZodTables, TTableId>[],
  ) {
    this.store.transaction(() => {
      this.deleteByCellValue(tableId, cellId, value);
      rows.forEach((row) => this.put(tableId, row));
    });
  }

  public replaceByFilter<TTableId extends StringKey<TZodTables>>(
    tableId: TTableId,
    filter: (row: TableRow<TZodTables, TTableId>) => boolean,
    rows: TableRow<TZodTables, TTableId>[],
  ) {
    this.store.transaction(() => {
      this.deleteByFilter(tableId, filter);
      rows.forEach((row) => this.put(tableId, row));
    });
  }

  public put<TTableId extends StringKey<TZodTables>>(
    tableId: TTableId,
    row: TableRow<TZodTables, TTableId>,
  ) {
    this.store.setRow(
      tableId,
      this.getRowId(tableId, row),
      cloneRow(row) as SchemaRow<TStoreSchemas[0], TTableId, true>,
    );
  }

  public get<TTableId extends StringKey<TZodTables>>(tableId: TTableId, rowId: ScalarId) {
    const strId = String(rowId);
    // store.getRow returns an empty object if the the row doesn't exist
    if (!this.store.hasRow(tableId, strId)) {
      return undefined;
    }
    return this.store.getRow(tableId, strId) as TableRow<TZodTables, TTableId>;
  }

  public getByCellValue<
    TTableId extends StringKey<TZodTables>,
    TCellId extends StringKey<TableRow<TZodTables, TTableId>>,
  >(
    tableId: TTableId,
    cellId: StringKey<TableRow<TZodTables, TTableId>>,
    value: TableRow<TZodTables, TTableId>[TCellId],
  ) {
    const table = this.getTable(tableId);

    return Object.values(table).filter((row) => row[cellId] === value);
  }

  public getTable<TTableId extends StringKey<TZodTables>>(tableId: TTableId) {
    return this.store.getTable(tableId) as Record<string, TableRow<TZodTables, TTableId>>;
  }

  public patch<TTableId extends StringKey<TZodTables>>(
    tableId: TTableId,
    row: PartialRowWithId<TZodTables, TTableId, TRowIdKeys>,
  ) {
    this.store.setPartialRow(
      tableId,
      this.getRowId(tableId, row),
      cloneRow(row) as SchemaRow<TStoreSchemas[0], TTableId, true>,
    );
  }

  public delete<TTableId extends StringKey<TZodTables>>(tableId: TTableId, rowId: ScalarId) {
    this.store.delRow(tableId, String(rowId));
  }

  public deleteByFilter<TTableId extends StringKey<TZodTables>>(
    tableId: TTableId,
    filter: (row: TableRow<TZodTables, TTableId>) => boolean,
  ) {
    this.store.transaction(() => {
      Object.entries(this.getTable(tableId)).forEach(([rowId, row]) => {
        if (filter(row)) {
          this.delete(tableId, rowId);
        }
      });
    });
  }

  public deleteByCellValue<
    TTableId extends StringKey<TZodTables>,
    TCellId extends StringKey<TableRow<TZodTables, TTableId>>,
  >(
    tableId: TTableId,
    cellId: StringKey<TableRow<TZodTables, TTableId>>,
    value: TableRow<TZodTables, TTableId>[TCellId],
  ) {
    this.deleteByFilter(tableId, (row) => (row[cellId] as unknown) === value);
  }

  protected getRowId<TTableId extends StringKey<TZodTables>>(
    tableId: TTableId,
    row: TableRow<TZodTables, TTableId> | PartialRowWithId<TZodTables, TTableId, TRowIdKeys>,
  ) {
    const idKey = this.rowIdKeys[tableId];

    if (!idKey) {
      throw Errors.serverError(`No ID key defined for ${tableId}`);
    }
    return <string>row[idKey];
  }

  public async asyncTransaction<TResult>(callback: () => Promise<TResult>): Promise<TResult> {
    this.store.startTransaction();

    try {
      return await callback();
    } finally {
      this.store.finishTransaction();
    }
  }
}
